import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { jobServices } from '../../services/jobServices';
import '../../css/Form.css';
import '../../css/JobPlan.css';
import { reportServices } from '../../services/reportServices';
import { GetApp } from '@mui/icons-material';
import { downloadReport } from '../../utils/downloadReport ';
import ActionProgressDialog from '../common/ActionProgressDialog';

const CalculateBudgetHours = ({ open, onClose, existingData, onSave, jobType, jobId, budgetUpdatedByUser, budgetUpdatedAt }) => {
  const [categories, setCategories] = useState([]);
  const [temporaryCategories, setTemporaryCategories] = useState([]);
  const [invalidInput, setInvalidInput] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState('');

  // Get existing data when modal is opened
  useEffect(() => {
    if (open) {
      if (existingData) {
        setCategories(existingData.categories || existingData);
        setTemporaryCategories(existingData.categories || existingData);
      } else {
        jobServices.getJobPlanCategorys(jobType).then((response) => {
          setCategories(response || []);
          setTemporaryCategories(response || []);
        }).catch((error) => {
          console.error("Error fetching categories", error);
        });
      }
    }
  }, [open, existingData]);

  // Centralized snackbar helper
  const showSnackbar = (message, severity = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);

    setTimeout(() => {
      setOpenSnackbar(false);
      setSnackbarMessage('');
    }, 4000);
  };

  const handeJobPlanDownload = async () => {
    setIsDownloading(true);
    setDownloadMessage('Generating Job Plan Report...');
    try {
      const response = await reportServices.downloadJobPlanReportPdf(jobId);
      downloadReport(response);
    } catch (error) {
      console.error("Error downloading PDF:", error);

      if (error?.response?.status === 403) {
        showSnackbar('Update status or task before downloading report.');
      } else {
        showSnackbar('Failed to download report. Please try again.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Convert HH:mm to minutes
  const convertToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
      return 0;
    }
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return -1;
    }
    return hours * 60 + minutes;
  };

  // Convert minutes back to HH:mm format
  const convertToTimeFormat = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Update budget hours with validation
  const handleBudgetChange = (categoryIndex, index, value) => {
    const updatedCategories = [...temporaryCategories];
    updatedCategories[categoryIndex].data[index].budgetHours = value;
    setTemporaryCategories(updatedCategories);
  };

  // Handle validation after editing (on blur or Enter key press)
  const validateTimeFormat = (categoryIndex, index, value) => {
    const validTimeFormat = /^([0-1]?[0-9]|2[0-3]):([0-5]?[0-9])$/; // HH:mm, HH in 00-23, mm in 00-59
    const minutes = convertToMinutes(value);

    if (!validTimeFormat.test(value)) {
      return 'Invalid time format. Please enter in HH:mm format.';
    } else if (minutes === -1) {
      return 'Invalid time. Hours should be between 00-23 and minutes between 00-59.';
    }
    return '';  // Clear error if format is valid
  };

  // Handle blur event (when focus leaves the input)
  const handleBlur = (categoryIndex, index) => {
    const key = `${categoryIndex}-${index}`;
    const value = temporaryCategories[categoryIndex].data[index].budgetHours;
    const errorMessage = validateTimeFormat(categoryIndex, index, value);
    if (errorMessage) {
      setInvalidInput(prev => ({
        ...prev,
        [key]: errorMessage,
      }));
    } else {
      setInvalidInput(prev => {
        const updatedErrors = { ...prev };
        delete updatedErrors[key];
        return updatedErrors;
      });
    }
  };

  // Handle input change (dynamically update error message)
  const handleChange = (categoryIndex, index, value) => {
    handleBudgetChange(categoryIndex, index, value);
    const errorMessage = validateTimeFormat(categoryIndex, index, value);

    if (errorMessage) {
      setInvalidInput(prev => ({
        ...prev,
        [`${categoryIndex}-${index}`]: errorMessage,
      }));
    } else {
      setInvalidInput(prev => {
        const updatedErrors = { ...prev };
        delete updatedErrors[`${categoryIndex}-${index}`];
        return updatedErrors;
      });
    }
  };

  // Calculate total time in minutes for a category
  const calculateCategoryTotal = (category) => {
    return category.data.reduce((total, item) => total + convertToMinutes(item.budgetHours), 0);
  };

  // Calculate merged total time in minutes
  const calculateMergedTotal = () => {
    return categories.reduce((total, category) => total + calculateCategoryTotal(category), 0);
  };

  const hasErrors = Object.keys(invalidInput).length > 0;

  // Save action
  const handleSave = () => {
    if (hasErrors) {
      showSnackbar('Invalid time format. Please enter in HH:mm format.');
    } else {
      let totalTime = convertToTimeFormat(calculateMergedTotal());
      setCategories([...temporaryCategories]);
      const updatedData = {
        categories,
        totalTime
      };
      onSave(updatedData);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const onCloseModal = () => {
    setTemporaryCategories([...categories]);
    onClose();
  };

  return (
    <React.Fragment>

      {snackbarMessage && <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>}

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          onCloseModal();
        }}
        slotProps={{ paper: { className: 'modern-dialog' } }}
        maxWidth="md"
        fullWidth
        aria-labelledby="budget-dialog-title"
      >
        <DialogTitle id="budget-dialog-title" className="modern-dialog-title">
          <span className="modern-dialog-title-text">{jobType} - Budget Plan</span>
          <IconButton
            aria-label="close"
            onClick={onCloseModal}
            className="modern-dialog-close-btn"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="modern-dialog-content">
          <Box sx={{ mt: 2 }}>
            {temporaryCategories.map((category, categoryIndex) => (
              <Box key={categoryIndex} sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'var(--primary)' }}>
                  {category.category}
                </Typography>

                <Box sx={{ overflowX: 'auto', width: '100%', borderRadius: '0.5rem', border: '1px solid #e2e8f0', mb: 2 }}>
                  <table className='checklist-table' style={{ border: 'none', margin: 0 }}>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Details</th>
                      <th>Budget Hours (HH:mm)</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.data.map((item, index) => (
                      <tr key={index}>
                        <td>{item.no}</td>
                        <td>{item.details}</td>
                        <td>
                          <input
                            type="text"
                            value={item.budgetHours}
                            onChange={(e) => handleChange(categoryIndex, index, e.target.value)}
                            onBlur={() => handleBlur(categoryIndex, index)}
                            maxLength="5"
                            placeholder="HH:mm"
                            className={`input-field ${invalidInput[`${categoryIndex}-${index}`] ? 'input-field-error' : 'input-field-valid'}`}
                            style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #e2e8f0' }}
                          />
                          {invalidInput[`${categoryIndex}-${index}`] && (
                            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
                              {invalidInput[`${categoryIndex}-${index}`]}
                            </Typography>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={item.remarks || ''}
                            onChange={(e) => {
                              const updatedCategories = [...temporaryCategories];
                              updatedCategories[categoryIndex].data[index].remarks = e.target.value;
                              setTemporaryCategories(updatedCategories);
                            }}
                            placeholder="Remarks"
                            className="input-field"
                            style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #e2e8f0' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </Box>
                <Box sx={{ mt: 1, textAlign: 'right', pr: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b' }}>
                    Category Total: {convertToTimeFormat(calculateCategoryTotal(category))} hours
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions className="modern-dialog-actions" sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 0 }, px: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ color: 'var(--primary)', fontWeight: 700 }}>
                Merged Total: {convertToTimeFormat(calculateMergedTotal())} hours
              </Typography>
              {budgetUpdatedAt && budgetUpdatedByUser && (
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                  Last Updated: {budgetUpdatedAt} by {budgetUpdatedByUser}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'row !important', sm: 'row' }, '& > button': { flex: { xs: 1, sm: 'none' }, margin: { xs: '0 !important', sm: '' } } }}>
              {existingData && (
                <Button
                  variant="contained"
                  onClick={handeJobPlanDownload}
                  sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}
                  startIcon={<GetApp />}
                >
                  Report
                </Button>
              )}
              <Button
                variant="outlined"
                color="inherit"
                onClick={onCloseModal}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
              >
                Continue
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
      <ActionProgressDialog open={isDownloading} message={downloadMessage} />
    </React.Fragment>
  );
};

export default CalculateBudgetHours;
